import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { doc, getDoc, collection, query, where, getDocs, updateDoc, increment, serverTimestamp, setDoc } from 'firebase/firestore';
import { useParams, useSearchParams } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Lock } from 'lucide-react';

const functions = getFunctions();
const createCheckoutSessionFn = httpsCallable(functions, 'createCheckoutSession');

const isDevelopment = process.env.NODE_ENV === 'development';

const Spinner = () => (
  <div style={{ width: '40px', height: '40px', border: '3px solid rgba(255,255,255,0.3)', borderTop: '3px solid white', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
);

const ButtonSpinner = () => (
  <div style={{ width: 24, height: 24, border: '3px solid rgba(255,255,255,0.3)', borderTop: '3px solid white', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
);

const BonusPage = () => {
  const { affiliateId, linkId } = useParams();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [bonusPhotoUrl, setBonusPhotoUrl] = useState(null);
  const [linkDocId, setLinkDocId] = useState(null);
  const [affiliateFirstName, setAffiliateFirstName] = useState('');
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [isCheckingUnlock, setIsCheckingUnlock] = useState(false);
  const [isPaying, setIsPaying] = useState(false);
  const [error, setError] = useState('');
  const [photoLoaded, setPhotoLoaded] = useState(false);
  const [profileImageLoading, setProfileImageLoading] = useState(true);
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

  const fingerprintFromUrl = searchParams.get('fingerprint');
  const isSuccess = searchParams.get('success') === 'true';

  const getFingerprint = () => {
    if (fingerprintFromUrl) return fingerprintFromUrl;
    return localStorage.getItem(`rating_fingerprint_${linkId}`);
  };

  const fingerprint = getFingerprint();

  // Viewport
  useEffect(() => {
    const update = () => setViewportHeight(window.visualViewport ? window.visualViewport.height : window.innerHeight);
    window.visualViewport?.addEventListener('resize', update);
    window.addEventListener('resize', update);
    update();
    return () => { window.visualViewport?.removeEventListener('resize', update); window.removeEventListener('resize', update); };
  }, []);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    return () => { document.body.style.overflow = ''; document.body.style.position = ''; document.body.style.width = ''; };
  }, []);

  // Load link data + check unlock
  useEffect(() => {
    const load = async () => {
      try {
        const linksQuery = query(collection(db, 'rating_links'), where('linkId', '==', linkId));
        const linkSnapshot = await getDocs(linksQuery);

        if (linkSnapshot.empty) { setError('Link not found'); setLoading(false); return; }

        const linkDoc = linkSnapshot.docs[0];
        const data = { id: linkDoc.id, ...linkDoc.data() };
        setLinkDocId(data.id);
        if (data.bonusPhotoUrl) setBonusPhotoUrl(data.bonusPhotoUrl);

        const affiliateDoc = await getDoc(doc(db, 'affiliates', affiliateId));
        if (affiliateDoc.exists()) {
          const ad = affiliateDoc.data();
          if (ad.firstName) setAffiliateFirstName(ad.firstName);
        }

        if (fingerprint) await checkUnlockStatus(data.id, fingerprint);

      } catch (e) {
        console.error('Error loading bonus page:', e);
        setError('Failed to load page');
      }

      setLoading(false);
    };

    load();
  }, [affiliateId, linkId, fingerprint]);

  // Poll after Stripe success redirect
  useEffect(() => {
    if (!isSuccess || !linkDocId || !fingerprint) return;

    let attempts = 0;
    const maxAttempts = 10;
    const interval = setInterval(async () => {
      attempts++;
      const unlocked = await checkUnlockStatus(linkDocId, fingerprint);
      if (unlocked || attempts >= maxAttempts) clearInterval(interval);
    }, 1500);

    return () => clearInterval(interval);
  }, [isSuccess, linkDocId, fingerprint]);

  const checkUnlockStatus = async (docId, fp) => {
    if (isDevelopment) return false;
    try {
      setIsCheckingUnlock(true);
      const unlockDoc = await getDoc(doc(db, 'bonus_unlocks', `${docId}_${fp}`));
      const unlocked = unlockDoc.exists();
      setIsUnlocked(unlocked);
      return unlocked;
    } catch (e) {
      console.error('Error checking unlock status:', e);
      return false;
    } finally {
      setIsCheckingUnlock(false);
    }
  };

  const handleUnlock = async () => {
    if (isPaying || !linkDocId || !fingerprint) return;
    setIsPaying(true);

    // Track unique unlock click per fingerprint per link
    try {
      const trackingRef = doc(db, 'unique_unlock_clicks', `${linkDocId}_${fingerprint}`);
      const trackingDoc = await getDoc(trackingRef);
      if (!trackingDoc.exists()) {
        await setDoc(trackingRef, {
          linkId: linkDocId,
          fingerprint,
          firstClickedAt: serverTimestamp(),
          clickCount: 1
        });
        await updateDoc(doc(db, 'rating_links', linkDocId), {
          totalUnlockClicks: increment(1),
          lastUnlockClickAt: serverTimestamp()
        });
      } else {
        await updateDoc(trackingRef, {
          clickCount: increment(1),
          lastClickedAt: serverTimestamp()
        });
      }
    } catch (e) { console.error('Error tracking unlock click:', e); }

    try {
      const result = await createCheckoutSessionFn({ linkDocId, linkId, affiliateId, fingerprint });
      if (result.data?.url) {
        window.location.href = result.data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (e) {
      console.error('Error creating checkout session:', e);
      setError('Something went wrong. Please try again.');
      setIsPaying(false);
    }
  };

  const possessive = affiliateFirstName ? `${affiliateFirstName}'s` : '';

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#10183C' }}>
      <Spinner />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  return (
    <div style={{
      position: 'fixed', inset: 0, overflow: 'hidden',
      fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
      backgroundColor: '#10183C',
      display: 'flex', flexDirection: 'column',
      height: `${viewportHeight}px`
    }}>
      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes spin { to { transform: rotate(360deg); } }
        * { box-sizing: border-box; -webkit-tap-highlight-color: transparent; }
      `}</style>

      {/* Hero photo — blurred if locked, full if unlocked */}
      <div style={{ flex: 1, position: 'relative' }}>
        {bonusPhotoUrl ? (
          <>
            <img
              src={bonusPhotoUrl}
              alt=""
              onLoad={() => setPhotoLoaded(true)}
              style={{
                position: 'fixed', inset: 0,
                width: '100%', height: '100%',
                objectFit: 'cover',
                filter: isUnlocked ? 'none' : 'blur(40px)',
                transform: isUnlocked ? 'none' : 'scale(1.18)',
                opacity: photoLoaded ? 1 : 0,
                transition: 'opacity 0.5s ease, filter 0.6s ease, transform 0.6s ease',
                zIndex: 0
              }}
            />
            {/* Dark overlay — only when locked */}
            {!isUnlocked && (
              <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(16, 24, 60, 0.52)', zIndex: 1 }} />
            )}
          </>
        ) : (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: '#10183C', zIndex: 0 }} />
        )}

        {/* Lock card — centered in photo area, hidden once unlocked */}
        {!isUnlocked && (
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            gap: 18, paddingBottom: 40,
            animation: 'fadeUp 0.5s ease 0.15s both',
            zIndex: 2
          }}>
            <div style={{
              width: 72, height: 72, borderRadius: '50%',
              backgroundColor: 'rgba(255,255,255,0.1)',
              border: '1.5px solid rgba(255,255,255,0.2)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <Lock size={32} color="white" strokeWidth={2.5} />
            </div>

            <p style={{
              color: 'white', fontSize: 22, fontWeight: 700,
              margin: 0, lineHeight: 1.35, letterSpacing: '-0.3px',
              textAlign: 'center', padding: '0 40px'
            }}>
              {possessive} bonus photo
            </p>
          </div>
        )}

        {/* Unlocked pill */}
        {isUnlocked && (
          <div style={{
            position: 'absolute', bottom: 40, left: 0, right: 0,
            display: 'flex', justifyContent: 'center',
            zIndex: 2, animation: 'fadeUp 0.4s ease'
          }}>
            <div style={{ backgroundColor: 'rgba(16,185,129,0.9)', borderRadius: '200px', padding: '10px 24px' }}>
              <span style={{ color: 'white', fontSize: 16, fontWeight: 700 }}>Bonus photo unlocked</span>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      {!isUnlocked && (
        <div style={{
          backgroundColor: '#1A2245',
          borderTopLeftRadius: '20px', borderTopRightRadius: '20px',
          padding: '20px 20px 40px',
          flexShrink: 0, position: 'relative', zIndex: 2,
          animation: 'fadeUp 0.5s ease 0.25s both'
        }}>
          {isSuccess && isCheckingUnlock ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, paddingVertical: 8 }}>
              <Spinner />
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 15, fontWeight: 500, margin: 0 }}>
                Confirming your payment...
              </p>
            </div>
          ) : (
            <>
              {error ? (
                <p style={{ color: '#FF6B6B', fontSize: 14, fontWeight: 600, margin: '0 0 12px', textAlign: 'center' }}>
                  {error}
                </p>
              ) : null}

              <button
                onClick={handleUnlock}
                disabled={isPaying}
                style={{
                  width: '100%', height: 58,
                  backgroundColor: isPaying ? '#2A3A6B' : '#4169E1',
                  border: 'none', borderRadius: 200,
                  color: 'white', fontSize: 20, fontWeight: 700,
                  cursor: isPaying ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: 'inherit', letterSpacing: '-0.2px',
                  transition: 'background-color 0.2s ease',

                }}
                onMouseDown={e => { if (!isPaying) e.currentTarget.style.transform = 'scale(0.98)'; }}
                onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                onTouchStart={e => { if (!isPaying) e.currentTarget.style.opacity = '0.85'; }}
                onTouchEnd={e => { e.currentTarget.style.opacity = '1'; }}
              >
                {isPaying ? <ButtonSpinner /> : 'Unlock for $1.99'}
              </button>


            </>
          )}
        </div>
      )}
    </div>
  );
};

export default BonusPage;